/**
 * KitDocumentCard Component
 * Displays a document card with download option
 *
 * @version 2025.1.0
 */

import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  CircularProgress,
} from '@mui/material';
import {
  Description as GuideIcon,
  Assignment as AsoIcon,
  MedicalServices as RecordIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Block as UnavailableIcon,
} from '@mui/icons-material';
import { KitDocument, DOCUMENT_LABELS } from '../../types/kit';
import { kitService } from '../../services/kitService';

const ICONS: Record<KitDocument['type'], React.ElementType> = {
  guide: GuideIcon,
  aso: AsoIcon,
  record: RecordIcon,
};

interface KitDocumentCardProps {
  document: KitDocument;
  token: string;
  /**
   * When true, an unavailable document is still being generated (polling
   * is active) — show a progress state instead of "não disponível".
   */
  processing?: boolean;
}

export const KitDocumentCard: React.FC<KitDocumentCardProps> = ({
  document,
  token,
  processing = false,
}) => {
  const [downloading, setDownloading] = useState(false);
  const Icon = ICONS[document.type];

  const handleDownload = () => {
    setDownloading(true);
    const url = kitService.getDocumentDownloadUrl(token, document.type);
    // Open in new tab for download
    window.open(url, '_blank');
    // Reset loading after a short delay
    setTimeout(() => setDownloading(false), 1000);
  };

  const handleView = () => {
    const url = kitService.getDocumentViewUrl(token, document.type);
    window.open(url, '_blank');
  };

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        opacity: document.available || processing ? 1 : 0.6,
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': document.available
          ? {
              transform: 'translateY(-2px)',
              boxShadow: 2,
            }
          : {},
      }}
    >
      <CardContent
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          py: 3,
        }}
      >
        {/* Icon */}
        <Box sx={{ mb: 2 }}>
          {document.available ? (
            <Icon sx={{ fontSize: 48, color: 'primary.main' }} />
          ) : processing ? (
            <CircularProgress size={48} />
          ) : (
            <UnavailableIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
          )}
        </Box>

        {/* Document Name */}
        <Typography variant="h6" gutterBottom>
          {DOCUMENT_LABELS[document.type]}
        </Typography>

        {/* Status / Actions */}
        {document.available ? (
          <Box sx={{ mt: 'auto', width: '100%' }}>
            <Button
              variant="contained"
              startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
              onClick={handleDownload}
              disabled={downloading}
              fullWidth
              sx={{ mb: 1 }}
            >
              {downloading ? 'Baixando...' : 'Baixar PDF'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<ViewIcon />}
              onClick={handleView}
              fullWidth
              size="small"
            >
              Visualizar
            </Button>
          </Box>
        ) : (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 'auto' }}
          >
            {processing ? 'Gerando documento…' : 'Documento não disponível'}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default KitDocumentCard;
